import { ChangeDetectorRef, Component, OnInit, OnDestroy, signal, computed, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { BusinessProxyService } from '../../proxies/finance/business.proxy.service';
import { ConfirmationService } from 'primeng/api';
import { EnumService } from '../../services/enum.service';
import {
  GlobalEntityDefinition,
  ModalService,
  DynamicFilterConfig,
  DynamicFilterValues,
  EnumValues,
  FilterOption,
  createDynamicFilterState,
} from '@card-software/library';
import { AuthService, debouncedServerSearch } from '@card-software/nexus-utilities';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { LocalHelperService } from '../../services/local-helper.service';
import { FormControl, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { LotItem } from '../../models/inventory/lot-item';
import { ItemMaster } from '../../models/item/item-master';
import { MatchingItemMasterCommand } from '../../models/item/yarn/yarn-search';
import { FabricQuantityDetail, KnittingOrder } from '../../models/production/knitting-order';
import { AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { AdjustWeightComponent } from '../common/modals/adjust-weight/adjust-weight.component';
import { KnittingOrderProxyService } from '../../proxies/production/knitting-order.proxy.service';
import { LotItemProxyService } from '../../proxies/inventory/lot-item.proxy.service';
import { YarnProxyService } from '../../proxies/item/yarn.proxy.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { Observable, Subject, catchError, finalize, map, of, takeUntil, throwError } from 'rxjs';
import { UserAuthenticationService } from '../../services/user-authentication.service';
import { UserGroup } from '../../models/authorization/enum/user-group.enum';
import { InventoryTransactionStatus } from '../../models/inventory/inventory-transaction';
import { ReturnSurplusSearchCommand } from '../../models/inventory/return-surplus-search';
import { ReturnSurplusRow } from '../../models/inventory/return-surplus-row';
import { TableLazyLoadEvent } from 'primeng/table';
import { InventoryTransactionProxyService } from '../../proxies/inventory/inventory-transaction.proxy.service';
import { InventoryReportProxyService } from '../../proxies/inventory/inventory-report.proxy.service';

// Interfaz para agrupar items únicos
interface GroupedItem {
  itemDescription: string;
  localizedDescription: string;
  manufacturerReference?: string;
  manufacturerName?: string;
  providerLotId?: string;
  totalNetQuantity: number;
  lotItems: LotItem[];
}

@Component({
  selector: 'app-return-surplus',
  templateUrl: './return-surplus.component.html',
  styleUrl: './return-surplus.component.scss',
})
export class ReturnSurplusComponent implements OnInit, OnDestroy {
  private formBuilder = inject(FormBuilder);
  private enumService = inject(EnumService);
  private translateService = inject(TranslateService);
  private router = inject(Router);
  private knittingOrderProxyService = inject(KnittingOrderProxyService);
  private lotItemProxyService = inject(LotItemProxyService);
  private yarnProxyService = inject(YarnProxyService);
  private inventoryTransactionProxyService = inject(InventoryTransactionProxyService);
  private inventoryReportProxyService = inject(InventoryReportProxyService);
  private modalService = inject(ModalService);
  private confirmationService = inject(ConfirmationService);
  private userAuthenticationService = inject(UserAuthenticationService);
  private localeHelperService = inject(LocalHelperService);
  private businessProxyService = inject(BusinessProxyService);
  private ctr = inject(ChangeDetectorRef);

  private destroy$ = new Subject<void>();
  isLoading = 0;
  isAdding = false;
  section: { label: string; value: number }[] = [];
  selectedSection = new FormControl(0);
  lotItemsWithSurplus: LotItem[] = [];

  allPendingTransactions: {
    transaction: ReturnSurplusRow;
    knittingOrder?: KnittingOrder;
  }[] = [];

  // Server-side pagination state for the Approvals table (lazy p-table).
  currentSearchCriteria: ReturnSurplusSearchCommand = {};
  private initialLoadDone = false;

  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'return-surplus-filters', config: this.filterConfig });
  showFilters = signal<boolean>(true);
  filteredKnittingOrdersForFilter = signal<KnittingOrder[]>([]);
  inventoryTransactionStatus = signal<EnumValues>({});

  statusOptions = computed<FilterOption[]>(() => {
    const statuses = this.inventoryTransactionStatus();
    if (Object.keys(statuses).length > 0) {
      return [
        { value: InventoryTransactionStatus.InRoute, label: statuses[InventoryTransactionStatus.InRoute] },
        { value: InventoryTransactionStatus.Received, label: statuses[InventoryTransactionStatus.Received] },
      ];
    }
    return [
      { value: InventoryTransactionStatus.InRoute, label: 'Pending Approval' },
      { value: InventoryTransactionStatus.Received, label: 'Received' },
    ];
  });

  // Cambiar a signal para que sea reactivo
  availableLotItems: LotItem[] = [];

  // Knitting Order Autocomplete
  knittingOrderSearch = new FormControl<KnittingOrder | null>(null);
  selectedKnittingOrder = signal<KnittingOrder | null>(null);

  // Lot Item Autocomplete
  lotItemSearch = new FormControl<LotItem | null>(null);
  selectedLotItem = signal<LotItem | null>(null);
  lotItemOptions = signal<LotItem[]>([]);

  // Form para el nuevo lot item
  newLotItemForm: FormGroup;

  groupedItems: GroupedItem[] = [];
  // Computed para items agrupados únicos

  // Computed simplificado para el valor del Excess Weight (sin formatear)
  excessWeight = new FormControl({ value: 0, disabled: true });

  isAddingLotItem = false;

  // Transfer tab properties
  targetKnittingOrderSearch = new FormControl<KnittingOrder | null>(null);
  selectedTargetKnittingOrder = signal<KnittingOrder | null>(null);
  yarnToItemMasterMap: Record<string, ItemMaster | null> = {};

  lotItemToTransactionMap: Record<string, { transactionId: string; status: InventoryTransactionStatus }> = {};

  get headerTitle(): string {
    return this.translateService.instant('Return_Surplus_Title');
  }

  asLotItem(item: unknown): LotItem {
    return item as LotItem;
  }

  currentPage = 1;
  pageSize = 25;
  totalRecords = 0;

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters));

    this.section = [
      {
        label: this.translateService.instant('Approvals'),
        value: 0,
      },
      {
        label: this.translateService.instant('Surplus'),
        value: 1,
      },
      {
        label: this.translateService.instant('Transfer'),
        value: 2,
      },
    ];

    // Inicializar el formulario
    this.newLotItemForm = this.formBuilder.group({
      groupedItem: [null, Validators.required],
      excessWeight: [{ value: 0, disabled: true }],
    });

    // Suscribirse a cambios en groupedItem para actualizar excessWeight
    this.newLotItemForm.get('groupedItem')?.valueChanges.subscribe((groupedItem: GroupedItem | null) => {
      if (groupedItem) {
        console.log('groupItem:', groupedItem);
        this.excessWeight.setValue(groupedItem?.totalNetQuantity || 0);

        this.newLotItemForm.patchValue(
          {
            excessWeight: groupedItem.totalNetQuantity,
          },
          { emitEvent: false },
        );
      } else {
        this.newLotItemForm.patchValue(
          {
            excessWeight: 0,
          },
          { emitEvent: false },
        );
      }
    });

    effect(
      () => {
        const statusesValue = this.enumService.getEnum('InventoryTransactionStatus', 'inventory').values;
        if (Object.keys(statusesValue).length > 0) {
          this.inventoryTransactionStatus.set(statusesValue);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.translateService.currentLang) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    // The dynamic-filters panel renders the autocomplete, so there is no element for
    // ServerSearchDirective: the shared operator is wired by hand and released with the component.
    this.knittingOrderFilterQueries$
      .pipe(
        // Solo buscar órdenes con estado In Progress, Completed o Closed
        debouncedServerSearch((query) => this.searchKnittingOrders(query, [2, 3, 4])),
        takeUntil(this.destroy$),
      )
      .subscribe((orders) => this.filteredKnittingOrdersForFilter.set(orders));

    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.tryInitializeFilterConfig();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'multiselect',
          formControlName: 'status',
          label: translate('Status', 'Status'),
          placeholder: translate('Select_status', 'Select status'),
          options: this.statusOptions(),
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'knittingOrder',
          label: translate('Knitting_Order', 'Knitting Order'),
          placeholder: translate('Select_knitting_order', 'Select knitting order'),
          autocompleteConfig: {
            suggestions: this.filteredKnittingOrdersForFilter(),
            optionLabel: 'knittingOrderNumber',
            optionValue: 'id',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.knittingOrderFilterQueries$.next(event.query),
            resolveById: (id: string) => this.resolveKnittingOrder(id),
            dropdown: true,
          },
          showClear: true,
          defaultVisible: false,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  private resolveKnittingOrder(id: string): Observable<KnittingOrder | null> {
    return this.knittingOrderProxyService.getById(id).pipe(
      map((response) => response.data ?? null),
      catchError((error: HttpErrorResponse) => (error.status === 404 ? of(null) : throwError(() => error))),
    );
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    this.currentSearchCriteria = this.buildSearchCriteria(filters);
    this.search();
  }

  /**
   * Loads the first page of return surplus transactions with the current criteria.
   * Used for the initial load and whenever filters change; pagination clicks go through onLazyLoad.
   */
  search(): void {
    this.currentPage = 1;
    this.initialLoadDone = true;
    this.loadReturnSurplus(1, this.pageSize);
  }

  /**
   * Lazy-load handler for the Approvals p-table. Fires on pagination (and once on table init,
   * which we skip in favor of the criteria-aware initial load triggered by the filter state).
   */
  onLazyLoad(event: TableLazyLoadEvent): void {
    if (!this.initialLoadDone) {
      // The table's automatic first lazy event runs before our filters are resolved.
      // The initial load is owned by search() so the correct criteria are applied.
      return;
    }
    const pageSize = event.rows || this.pageSize;
    const pageNumber = Math.floor((event.first || 0) / pageSize) + 1;
    this.loadReturnSurplus(pageNumber, pageSize);
  }

  private buildSearchCriteria(filters: DynamicFilterValues): ReturnSurplusSearchCommand {
    const statuses = filters['status'] as number[] | null | undefined;
    const knittingOrder = filters['knittingOrder'] as string | { id?: string } | null | undefined;
    const knittingOrderId = typeof knittingOrder === 'string' ? knittingOrder : knittingOrder?.id;

    const statusesToSearch =
      statuses && statuses.length > 0 ? statuses : [InventoryTransactionStatus.InRoute, InventoryTransactionStatus.Received];

    return {
      statuses: statusesToSearch,
      knittingOrderReference: knittingOrderId || undefined,
    };
  }

  private loadReturnSurplus(pageNumber: number, pageSize: number): void {
    this.currentPage = pageNumber;
    this.pageSize = pageSize;
    this.startLoading();

    this.lotItemProxyService
      .searchReturnSurplus(this.currentSearchCriteria, pageNumber, pageSize)
      .pipe(
        finalize(() => {
          this.stopLoading();
        }),
      )
      .subscribe({
        next: (response) => {
          const paginated = response.data;
          const rows = paginated?.items ?? [];
          this.totalRecords = paginated?.totalCount ?? 0;
          this.bindRowsWithKnittingOrders(rows);
        },
        error: (error) => {
          console.error('Error searching transactions:', error);
          this.allPendingTransactions = [];
          this.totalRecords = 0;
        },
      });
  }

  /**
   * Resolves the knitting order number for the current page's rows. Only the page's ids are sent
   * and the lightweight flag avoids the heavy roll/HTTP enrichment (the table only needs the number).
   */
  private bindRowsWithKnittingOrders(rows: ReturnSurplusRow[]): void {
    const knittingOrderIds = Array.from(
      new Set(rows.map((row) => row.initiatorReference).filter((id): id is string => id !== undefined && id !== null)),
    );

    if (knittingOrderIds.length === 0) {
      this.allPendingTransactions = rows.map((transaction) => ({ transaction, knittingOrder: undefined }));
      return;
    }

    this.isLoading++;
    this.knittingOrderProxyService.getByIds(knittingOrderIds, true).subscribe({
      next: (knittingOrdersResponse) => {
        this.isLoading--;
        const knittingOrderMap = new Map<string, KnittingOrder>();
        knittingOrdersResponse.data?.forEach((ko) => {
          if (ko.id) {
            knittingOrderMap.set(ko.id, ko);
          }
        });

        this.allPendingTransactions = rows.map((transaction) => ({
          transaction,
          knittingOrder: transaction.initiatorReference ? knittingOrderMap.get(transaction.initiatorReference) : undefined,
        }));
      },
      error: (error) => {
        console.error('Error fetching knitting orders:', error);
        this.isLoading--;
        this.allPendingTransactions = rows.map((transaction) => ({ transaction, knittingOrder: undefined }));
      },
    });
  }

  startLoading(): void {
    this.isLoading++;
  }

  stopLoading(): void {
    this.isLoading = Math.max(0, this.isLoading - 1);
  }

  private searchKnittingOrders(query: string, statuses: number[]): Observable<KnittingOrder[]> {
    return this.knittingOrderProxyService
      .search({ knittingOrderNumber: query, statuses })
      .pipe(map((response) => response.data?.items || []));
  }

  // Dispatcher: delega según la tab activa. Tab Transfer: la KO origen debe ser Closed (4)
  // obligatoriamente; en el resto vale In Progress, Completed o Closed.
  public fetchSourceKnittingOrders = (query: string): Observable<KnittingOrder[]> =>
    this.searchKnittingOrders(query, this.selectedSection.value === 2 ? [4] : [2, 3, 4]);

  private readonly knittingOrderFilterQueries$ = new Subject<string>();

  onKnittingOrderSelected(event?: AutoCompleteSelectEvent): void {
    if (event && event.value) {
      this.selectedKnittingOrder.set(event.value);
      this.loadAvailableLotItems(event.value.id!);
      this.getReturnedLotItems(event.value.id!);

      // Reset del formulario cuando se cambia el knitting order
      this.newLotItemForm.reset();
    } else {
      this.selectedKnittingOrder.set(null);
      this.availableLotItems = [];
      this.newLotItemForm.reset();
    }
  }

  loadAvailableLotItems(knittingOrderId: string): void {
    this.lotItemProxyService.getLotItemByKnittingOrderId(knittingOrderId).subscribe({
      next: (response) => {
        if (response.data) {
          const items = Array.isArray(response.data) ? response.data : [response.data];

          // Agregar fieldToDisplay a cada item
          items.forEach((l) => (l.fieldToDisplay = this.localeHelperService.getLocalizedValue(l.item?.itemDescription)));

          // Actualizar el signal
          this.availableLotItems = items;
        }
        this.groupedItems = this.getGroupedItemsOptions();
      },
      error: (error) => {
        console.error(error);
        this.availableLotItems = [];
      },
    });
  }

  getReturnedLotItems(knittingOrderId: string): void {
    // Search for inventory transactions related to this knitting order
    this.inventoryTransactionProxyService
      .getRelatedInventoryTransactionsByEntityKey({
        entityDefinitionCode: GlobalEntityDefinition.KnittingOrder,
        reference: knittingOrderId,
      })
      .subscribe({
        next: (response) => {
          if (response.data) {
            // Clear previous mapping
            this.lotItemToTransactionMap = {};
            this.lotItemsWithSurplus = [];

            // Create mapping from lot item number to transaction ID
            // and collect all lot item numbers to fetch
            const lotItemNumbers: string[] = [];

            response.data.forEach((transaction) => {
              transaction.lines?.forEach((line) => {
                if (line.lotItemKey?.number && transaction.id) {
                  this.lotItemToTransactionMap[line.lotItemKey.number] = {
                    transactionId: transaction.id,
                    status: transaction.status!,
                  };
                  lotItemNumbers.push(line.lotItemKey.number);
                }
              });
            });

            // Fetch the actual lot items
            if (lotItemNumbers.length > 0) {
              this.lotItemProxyService.getByLotNumbers(lotItemNumbers).subscribe({
                next: (lotItemResponse) => {
                  if (lotItemResponse.data) {
                    this.lotItemsWithSurplus = lotItemResponse.data;
                  }
                },
                error: (error) => {
                  console.error('Error fetching lot items:', error);
                },
              });
            }
          }
        },
        error: (error) => {
          console.error('Error fetching inventory transactions:', error);
        },
      });
  }

  onLotItemSelected(event?: AutoCompleteSelectEvent): void {
    if (event && event.value) {
      this.selectedLotItem.set(event.value);
    } else {
      this.selectedLotItem.set(null);
    }
  }

  onClickCancel(): void {
    this.router.navigate(['/inventory']);
  }

  onClickOpenModal(): void {
    // Verificar que el formulario sea válido antes de abrir el modal
    if (this.newLotItemForm.invalid) {
      GlobalMessageUtil.warn('Form Incomplete', 'Please select an item description first');
      return;
    }

    const formData = this.newLotItemForm.getRawValue();
    const groupedItem: GroupedItem = formData.groupedItem;

    // Abrir modal pasando el MaxWeight
    this.modalService.open(AdjustWeightComponent, {}, { MaxWeight: formData.excessWeight }).subscribe({
      next: (result) => {
        if (result && result.action === 'save' && result.data) {
          const newWeight = result.data;

          // Validar que haya un knitting order seleccionado
          if (!this.selectedKnittingOrder()) {
            GlobalMessageUtil.error('No Knitting Order Selected', 'Please select a knitting order first');
            return;
          }

          // Obtener el primer lot item del grupo para copiar su número
          const lotItemToCopy = groupedItem.lotItems[0];

          if (!lotItemToCopy || !lotItemToCopy.number) {
            GlobalMessageUtil.error('Error', 'No valid lot item found to copy');
            return;
          }

          // Llamar al servicio para crear el nuevo lot item
          this.createNewLotItem(this.selectedKnittingOrder()!.id!, newWeight, lotItemToCopy.number);
          this.isAdding = false;
          this.newLotItemForm.reset();
        }
      },
      error: (error) => {
        console.error('Error handling modal result:', error);
      },
    });
  }

  createNewLotItem(knittingOrderId: string, newLotItemQuantity: number, lotItemNumberToCopy: string): void {
    this.isLoading++;

    this.lotItemProxyService
      .CreateNewLotItem(knittingOrderId, newLotItemQuantity, lotItemNumberToCopy)
      .pipe(
        finalize(() => {
          this.isLoading--;
        }),
      )
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            GlobalMessageUtil.success('Lot Item Created', `Lot item ${response.data.number} created successfully`);

            // Resetear el formulario
            this.newLotItemForm.reset();

            // Recargar los lot items disponibles y la lista de surplus con sus transacciones
            if (this.selectedKnittingOrder()) {
              this.loadAvailableLotItems(this.selectedKnittingOrder()!.id!);
              this.getReturnedLotItems(this.selectedKnittingOrder()!.id!);
            }
          }
        },
        error: (error) => {
          console.error(error);
        },
      });
  }

  getGroupedItemsOptions() {
    const items = this.availableLotItems;
    if (!items || items.length === 0) {
      return [];
    }

    // Agrupar por itemDescription + manufacturer + providerLotId
    const grouped = items.reduce(
      (acc, lotItem) => {
        const itemDesc = lotItem.item?.itemDescription;
        if (!itemDesc) return acc;

        const localizedDesc = this.localeHelperService.getLocalizedValue(itemDesc);
        const manufacturerRef = lotItem.manufacturerReference ?? '';
        const manufacturerName = lotItem.manufacturer?.name ?? '';
        const providerLotId = lotItem.providerLotId ?? '';
        const key = `${localizedDesc}||${manufacturerRef}||${providerLotId}`;

        if (!acc[key]) {
          acc[key] = {
            itemDescription: JSON.stringify(itemDesc),
            localizedDescription: localizedDesc,
            manufacturerReference: manufacturerRef || undefined,
            manufacturerName: manufacturerName || undefined,
            providerLotId: providerLotId || undefined,
            totalNetQuantity: 0,
            lotItems: [],
          };
        }

        acc[key].totalNetQuantity += lotItem.netQuantity || 0;
        acc[key].lotItems.push(lotItem);

        return acc;
      },
      {} as Record<string, GroupedItem>,
    );

    // Ordenar por descripción, luego manufacturer, luego lot para agrupar visualmente
    return Object.values(grouped).sort((a, b) => {
      const byDesc = a.localizedDescription.localeCompare(b.localizedDescription);
      if (byDesc !== 0) return byDesc;
      const byMfr = (a.manufacturerName ?? '').localeCompare(b.manufacturerName ?? '');
      if (byMfr !== 0) return byMfr;
      return (a.providerLotId ?? '').localeCompare(b.providerLotId ?? '');
    });
  }

  // Transfer tab methods
  // Tab Transfer: solo Approved (2) e InProgress (3)
  public fetchTargetKnittingOrders = (query: string): Observable<KnittingOrder[]> => this.searchKnittingOrders(query, [2, 3]);

  onTargetKnittingOrderSelected(event?: AutoCompleteSelectEvent): void {
    if (event && event.value) {
      const knittingOrder: KnittingOrder = event.value;
      this.selectedTargetKnittingOrder.set(knittingOrder);

      // Fetch item masters for yarn codes in this knitting order
      this.fetchItemMastersForKnittingOrder(knittingOrder);
    } else {
      this.selectedTargetKnittingOrder.set(null);
      this.yarnToItemMasterMap = {};
    }
  }

  fetchItemMastersForKnittingOrder(knittingOrder: KnittingOrder): void {
    const distinctYarnCodes = knittingOrder.fabricQuantityDetails
      .flatMap((fabric) => fabric.yarnAndProviderInfos?.map((yarn) => yarn.yarnCode) || [])
      .filter((yarnCode, index, self) => yarnCode && self.indexOf(yarnCode) === index);

    if (distinctYarnCodes.length === 0) {
      return;
    }

    const command: MatchingItemMasterCommand[] = distinctYarnCodes.map((yarnCode) => ({
      yarnCode: yarnCode!,
    }));

    this.isLoading++;
    this.yarnProxyService
      .getItemMastersByYarnCode(command)
      .pipe(finalize(() => this.isLoading--))
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.yarnToItemMasterMap = response.data;
          }
        },
        error: (error) => {
          console.error('Error fetching item masters:', error);
        },
      });
  }

  getNotes(lotItem: LotItem): string {
    return lotItem.notes && lotItem.notes.length > 0 ? lotItem.notes.flatMap((n) => n.richText).join(';') : '';
  }

  getEntryItemCode(entry: FabricQuantityDetail): ItemMaster | null {
    if (!entry.yarnAndProviderInfos || entry.yarnAndProviderInfos.length === 0) {
      return null;
    }

    const yarnCode = entry.yarnAndProviderInfos[0].yarnCode;
    return this.yarnToItemMasterMap[yarnCode] || null;
  }

  getEntryItemCodes(entry: FabricQuantityDetail): (ItemMaster | null)[] {
    if (!entry.yarnAndProviderInfos || entry.yarnAndProviderInfos.length === 0) {
      return [];
    }

    return entry.yarnAndProviderInfos.map((yarnInfo) => {
      return this.yarnToItemMasterMap[yarnInfo.yarnCode] || null;
    });
  }

  isEntryDisabledForTransfer(entry: FabricQuantityDetail): boolean {
    const selectedGroupedItem: GroupedItem | null = this.newLotItemForm.get('groupedItem')?.value;

    if (!selectedGroupedItem || !entry.yarnAndProviderInfos || selectedGroupedItem.lotItems.length === 0) {
      return true;
    }

    // Get the sample lot item to compare against
    const sampleLotItem = selectedGroupedItem.lotItems[0];
    const selectedItemCode = sampleLotItem?.item?.itemCode;
    const selectedManufacturer = sampleLotItem?.manufacturer?.name;
    const selectedProviderLotId = sampleLotItem?.providerLotId;

    if (!selectedItemCode) {
      return true;
    }

    // Check if any yarn in the entry matches the item code, manufacturer, and provider lot number
    const matches = entry.yarnAndProviderInfos.some((yarnInfo) => {
      const yarnItemMaster = this.yarnToItemMasterMap[yarnInfo.yarnCode];
      const itemCodeMatches = yarnItemMaster?.itemCode === selectedItemCode;
      const manufacturerMatches = yarnInfo.provider?.name === selectedManufacturer;
      const providerLotMatches = yarnInfo.providerLotNumber === selectedProviderLotId;

      return itemCodeMatches && manufacturerMatches && providerLotMatches;
    });

    return !matches;
  }

  hasAnyMatchingItemCode(): boolean {
    const selectedGroupedItem: GroupedItem | null = this.newLotItemForm.get('groupedItem')?.value;
    const targetKnittingOrder = this.selectedTargetKnittingOrder();

    if (!selectedGroupedItem || !targetKnittingOrder || selectedGroupedItem.lotItems.length === 0) {
      return false;
    }

    const selectedItemCode = selectedGroupedItem.lotItems[0]?.item?.itemCode;
    if (!selectedItemCode) {
      return false;
    }

    return targetKnittingOrder.fabricQuantityDetails.some((entry) =>
      entry.yarnAndProviderInfos.some((yarnInfo) => {
        const yarnItemMaster = this.yarnToItemMasterMap[yarnInfo.yarnCode];
        return yarnItemMaster?.itemCode === selectedItemCode;
      }),
    );
  }

  hasMatchingManufacturerAndLot(): boolean {
    const selectedGroupedItem: GroupedItem | null = this.newLotItemForm.get('groupedItem')?.value;
    const targetKnittingOrder = this.selectedTargetKnittingOrder();

    if (!selectedGroupedItem || !targetKnittingOrder || selectedGroupedItem.lotItems.length === 0) {
      return false;
    }

    const sampleLotItem = selectedGroupedItem.lotItems[0];
    const selectedItemCode = sampleLotItem?.item?.itemCode;
    const selectedManufacturer = sampleLotItem?.manufacturer?.name;
    const selectedProviderLotId = sampleLotItem?.providerLotId;

    if (!selectedItemCode) {
      return false;
    }

    return targetKnittingOrder.fabricQuantityDetails.some((entry) =>
      entry.yarnAndProviderInfos.some((yarnInfo) => {
        const yarnItemMaster = this.yarnToItemMasterMap[yarnInfo.yarnCode];
        const itemCodeMatches = yarnItemMaster?.itemCode === selectedItemCode;
        const manufacturerMatches = yarnInfo.provider?.name === selectedManufacturer;
        const providerLotMatches = yarnInfo.providerLotNumber === selectedProviderLotId;

        return itemCodeMatches && manufacturerMatches && providerLotMatches;
      }),
    );
  }

  onTransferToEntry(entry: FabricQuantityDetail): void {
    const selectedGroupedItem: GroupedItem | null = this.newLotItemForm.get('groupedItem')?.value;

    if (!selectedGroupedItem) {
      GlobalMessageUtil.warn('No Item Selected', 'Please select an item first');
      return;
    }

    if (this.isEntryDisabledForTransfer(entry)) {
      GlobalMessageUtil.warn('Item Mismatch', 'Selected entry does not match the item code');
      return;
    }

    const maxWeight = selectedGroupedItem.totalNetQuantity;

    // Open modal to get the transfer quantity
    this.openTransferModal(selectedGroupedItem, entry, maxWeight);
  }

  private openTransferModal(selectedGroupedItem: GroupedItem, entry: FabricQuantityDetail, maxWeight: number): void {
    this.modalService.open(AdjustWeightComponent, {}, { MaxWeight: maxWeight }).subscribe({
      next: (result) => {
        if (result && result.action === 'save' && result.data) {
          const transferQuantity = result.data;
          this.executeTransferFromForm(selectedGroupedItem, entry, transferQuantity, maxWeight);
        }
      },
      error: (error) => {
        console.error('Error handling modal result:', error);
      },
    });
  }

  executeTransferFromForm(groupedItem: GroupedItem, targetEntry: FabricQuantityDetail, quantity: number, maxWeight: number): void {
    const sourceKnittingOrder = this.selectedKnittingOrder();
    const targetKnittingOrder = this.selectedTargetKnittingOrder();

    if (!sourceKnittingOrder || !targetKnittingOrder) {
      GlobalMessageUtil.error('Error', 'Missing source or target knitting order');
      return;
    }

    // Get the first lot item as a sample
    const lotItemToCopy = groupedItem.lotItems[0];
    if (!lotItemToCopy || !lotItemToCopy.number) {
      GlobalMessageUtil.error('Error', 'No valid lot item found');
      return;
    }

    this.isLoading++;

    this.lotItemProxyService
      .transferLotItemBetweenKnittingOrders(
        sourceKnittingOrder.id,
        targetKnittingOrder.id,
        targetEntry.fabricCode,
        targetEntry.variantCode,
        lotItemToCopy.number!,
        quantity,
      )
      .pipe(
        finalize(() => {
          this.isLoading--;
        }),
      )
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            GlobalMessageUtil.success('Transfer Successful', `Successfully transferred ${quantity} lbs`);

            this.lotItemsWithSurplus.push(response.data);
            // Reset form and reload
            this.newLotItemForm.reset();
            this.isAdding = false;

            if (sourceKnittingOrder.id) {
              this.loadAvailableLotItems(sourceKnittingOrder.id);
            }
          }
        },
        error: (e) => {
          console.error(e);

          // Reopen the modal to allow user to try again
          this.openTransferModal(groupedItem, targetEntry, maxWeight);
        },
      });
  }

  /**
   * Checks if the current user has permission to receive/approve return surplus transactions
   */
  canEdit = computed(() => AuthService.hasRole(UserGroup.ReturnSurplusAdmin));

  canReceiveReturnSurplus = computed(() => {
    return AuthService.hasRole(UserGroup.RawMaterialWarehouseAdmin);
  });

  /**
   * Gets the status of a lot item based on its inventory transaction
   */
  getLotItemStatus(lotItem: LotItem): InventoryTransactionStatus | null {
    if (!lotItem.number) {
      return null;
    }

    const transactionInfo = this.lotItemToTransactionMap[lotItem.number];
    return transactionInfo ? transactionInfo.status : null;
  }

  /**
   * Checks if a lot item is pending approval (InRoute status)
   */
  isPendingApproval(lotItem: LotItem): boolean {
    return this.getLotItemStatus(lotItem) === InventoryTransactionStatus.InRoute;
  }

  /**
   * Receives (approves) a return surplus transaction
   */
  onReceiveReturnSurplus(lotItem: LotItem): void {
    const transactionInfo = lotItem.number ? this.lotItemToTransactionMap[lotItem.number] : undefined;

    if (!transactionInfo) {
      GlobalMessageUtil.error('Error', 'Cannot find transaction ID for this lot item');
      return;
    }

    this.confirmationService.confirm({
      key: 'main',
      message: this.translateService.instant('Confirm Receive Return Surplus', {
        defaultValue: `Are you sure you want to approve and receive lot item ${lotItem.number}?`,
      }),
      header: this.translateService.instant('Confirm Receipt'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.receiveReturnSurplusTransaction(transactionInfo.transactionId);
      },
    });
  }

  /**
   * Calls the API to receive the return surplus transaction
   */
  private receiveReturnSurplusTransaction(transactionId: string): void {
    this.isLoading++;

    this.lotItemProxyService
      .receiveReturnSurplus(transactionId)
      .pipe(
        finalize(() => {
          this.isLoading--;
        }),
      )
      .subscribe({
        next: (response) => {
          if (response.success) {
            GlobalMessageUtil.success(
              this.translateService.instant('Success'),
              this.translateService.instant('Return Surplus Received Successfully'),
            );

            // Reload the lot items to reflect the new status
            if (this.selectedKnittingOrder()) {
              this.getReturnedLotItems(this.selectedKnittingOrder()!.id!);
            }

            // Also reload the approvals list
            this.search();
          }
        },
        error: (error) => {
          console.error(error);
        },
      });
  }

  /**
   * Approves a transaction from the approvals list
   */
  onApproveTransaction(transactionId: string, knittingOrderNumber: string): void {
    this.confirmationService.confirm({
      key: 'main',
      message: this.translateService.instant('Confirm Receive Return Surplus', {
        defaultValue: `Are you sure you want to approve and receive surplus from knitting order ${knittingOrderNumber}?`,
      }),
      header: this.translateService.instant('Confirm Receipt'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.receiveReturnSurplusTransaction(transactionId);
      },
    });
  }

  /**
   * Downloads production transfer report for a transaction
   */
  onDownloadReport(transactionId: string): void {
    // Find the transaction in the current list
    const transactionData = this.allPendingTransactions.find((t) => t.transaction.id === transactionId);

    if (!transactionData || !transactionData.transaction.lines || transactionData.transaction.lines.length === 0) {
      GlobalMessageUtil.error('Error', 'No lot items found for this transaction');
      return;
    }

    // Get the first lot item number from the transaction lines
    const firstLotItemNumber = transactionData.transaction.lines[0]?.lotItemNumber;

    if (!firstLotItemNumber) {
      GlobalMessageUtil.error('Error', 'Invalid lot item number');
      return;
    }

    this.isLoading++;

    this.inventoryReportProxyService
      .productionTransferReport(firstLotItemNumber, 'lot-item', 'knitting-to-raw-material')
      .pipe(
        finalize(() => {
          this.isLoading--;
        }),
      )
      .subscribe({
        next: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `SurplusReturn_${firstLotItemNumber}_${new Date().getTime()}.pdf`;
          link.click();
          window.URL.revokeObjectURL(url);
          GlobalMessageUtil.success('Success', 'Report downloaded successfully');
        },
        error: (error) => {
          console.error('Error downloading report:', error);
        },
      });
  }

  /**
   * Gets badge severity based on transaction status
   */
  getStatusBadgeSeverity(status: InventoryTransactionStatus | null): 'success' | 'warn' | 'info' {
    switch (status) {
      case InventoryTransactionStatus.Received:
        return 'success';
      case InventoryTransactionStatus.InRoute:
        return 'warn';
      default:
        return 'info';
    }
  }

  /**
   * Gets status label for display
   */
  getStatusLabel(status: InventoryTransactionStatus | null): string {
    switch (status) {
      case InventoryTransactionStatus.Received:
        return this.translateService.instant('Received');
      case InventoryTransactionStatus.InRoute:
        return this.translateService.instant('Pending Approval');
      default:
        return this.translateService.instant('Unknown');
    }
  }

  /**
   * Checks if the report can be downloaded for the given transaction status
   */
  canDownloadReport(status: InventoryTransactionStatus | null): boolean {
    return status === InventoryTransactionStatus.InRoute || status === InventoryTransactionStatus.Received;
  }
}
